import Modal from "@/components/controls/Modal";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { Close } from "@dreamhub/designsystem/icons";
import toast from "react-hot-toast";
import { UserData } from "@/entities/users/types";
import { PartnerData, PartnerStatus } from "../types";
import { useCreatePartnerMutation } from "../hooks";
import { normalizePartnerTypeAndStatus } from "../helpers";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { mapEmptyToNull } from "@/utils/formatting";
import { useEntityFields } from "@/pages/settings/hooks";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext,
  buildMapEmptyToNullObject,
  getFieldNameByDisplayType
} from "@/utils/entityFieldsUtils/formFields";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import useBoundStore from "@/services/store/BoundStore";
import CreatePartnerContactModal from "@/pages/partners/contacts/components/CreatePartnerContactModal";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import { createUnsavedChangesModalSetter } from "@/entities/modals/helpers";
import { IconButton } from "@/components/ui/icon-button";
import { useNavigate } from "react-router-dom";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetPage?: () => void;
  onSuccess?: (id: string, name: string) => void;
  defaultName?: string;
}

type FormValues = Record<string, unknown>;
type FormSetValue = (
  fieldName: string,
  value: unknown,
  options?: { shouldValidate?: boolean }
) => void;
type FormGetValues = () => FormValues;

const CreatePartnerModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  resetPage,
  onSuccess,
  defaultName
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const createPartner = useCreatePartnerMutation();
  const navigate = useNavigate();

  const [formGetValues, setFormGetValues] = useState<FormGetValues | null>(
    null
  );
  const [formSetValue, setFormSetValue] = useState<FormSetValue | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState<"partner" | "partnerContacts">("partner");

  const { data: entityFieldsResponse } = useEntityFields(EntityType.PARTNER);
  const users = useBoundStore((state) => state.users.entities);

  const formFieldsContext: CreateFormNewContext = {
    users,
    userData: {} as UserData,
    selectedCompanyId: "",
    selectedPersonId: "",
    setSelectedCompanyId: () => {},
    setSelectedPersonId: () => {},
    setHasUserEnteredDealName: () => {},
    autoPopulateDealName: () => {},
    setView: (nextView) => {
      if (nextView === "partnerContacts") {
        setView("partnerContacts");
      }
    },
    hasUserEnteredDealName: false,
    entityType: EntityType.PARTNER
  };

  const baseSections = createFormNewSectionsFromAPI(
    entityFieldsResponse?.fields,
    t,
    formFieldsContext
  );

  const partnerContactsFieldName = getFieldNameByDisplayType(
    baseSections,
    FieldDisplayType.PARTNER_CONTACTS
  );

  const onPartnerContactCreation = (id: string, name: string) => {
    if (!id || !name || !partnerContactsFieldName) return;
    if (
      typeof formSetValue === "function" &&
      typeof formGetValues === "function"
    ) {
      const existing = formGetValues()?.[partnerContactsFieldName];
      const safeArray = Array.isArray(existing) ? existing : [];
      formSetValue(partnerContactsFieldName, [...safeArray, id], {
        shouldValidate: true
      });
    }
  };

  const formSections: FormSectionProps[] = baseSections.map((section) => ({
    ...section,
    fields: (section.fields ?? []).map(
      (field: FormFieldNewType): FormFieldNewType => ({
        ...field,
        labelPosition: "left",
        layout: "flex",
        ...(field.name === "name" && defaultName
          ? { defaultValue: defaultName }
          : {}),
        // Options for Status come from partners_statuses via its FK, but which one a
        // new partner starts on is a product decision, not a property of the lookup
        // table — so it stays here rather than in the field's stored config. Matched
        // against the option ids by SingleSelect, which stringifies first.
        ...(field.name === "status"
          ? { defaultValue: PartnerStatus.ACTIVE }
          : {})
      })
    )
  }));

  const { handleCloseAttempt, warningDialogProps } = useUnsavedChanges(
    () => formGetValues?.() || {},
    formSections
  );

  useEffect(() => {
    if (defaultName && typeof formSetValue === "function") {
      formSetValue("name", defaultName, { shouldValidate: true });
    }
  }, [defaultName, formSetValue]);

  const hasStatusField = baseSections.some((section) =>
    section.fields?.some((field) => field.name === "status")
  );

  // FormNew reads the Active default only at mount, so re-apply it once the form and the Status field both exist.
  useEffect(() => {
    if (hasStatusField && typeof formSetValue === "function") {
      formSetValue("status", PartnerStatus.ACTIVE, { shouldValidate: true });
    }
  }, [hasStatusField, formSetValue]);

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
    } else {
      formRef.current?.reset();
      setFormSetValue(null);
      setFormGetValues(null);
      setIsFormValid(false);
      setView("partner");
    }
  }, [isOpen]);

  const handleSubmit = async (data: PartnerData) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    const transformFormData: PartnerData = {
      ...data,
      ...mapEmptyToNull(buildMapEmptyToNullObject(baseSections, data)),
      ...normalizePartnerTypeAndStatus(data)
    };

    await createPartner.mutateAsync(transformFormData, {
      onSuccess: async (response) => {
        await resetPage?.();
        onSuccess?.(response.id, data.name);
        toast.success(
          <div className="flex gap-4 items-center">
            <div>{`${data.name} ${t("partners.createModal.success")}`}</div>
            <Button
              className="size-fit p-1"
              onClick={() => {
                toast.dismiss();
                navigate(`/partners/${response?.id}`);
              }}
              variant="primary"
            >
              {t("partners.createModal.view")}
            </Button>
          </div>,
          { duration: 5000 }
        );
        setIsOpen(false);
      },
      onError: () => {
        setIsSubmitting(false);
        toast.error(
          () => (
            <div className="flex items-center gap-2">
              <div>{t("partners.createModal.error")}</div>
              <IconButton
                size="md"
                onClick={() => toast.dismiss()}
                variant="iconNoBg"
              >
                <Close />
              </IconButton>
            </div>
          ),
          { icon: null }
        );
      }
    });
  };

  const handleCancel = () => {
    handleCloseAttempt(() => {
      setIsOpen(false);
    });
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  const handleSetValue = (formMethods: {
    setValue: FormSetValue;
    getValues: FormGetValues;
  }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
    if (typeof formMethods.getValues === "function") {
      setFormGetValues(() => formMethods.getValues);
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={createUnsavedChangesModalSetter(
          setIsOpen,
          handleCloseAttempt
        )}
        data-test="create-partner-modal"
        title={
          <Text as="div" variant="lgSubtitleH1">
            {t("partners.createModal.title")}
          </Text>
        }
        content={
          <div
            className="flex flex-col gap-3 p-6 h-[60vh] overflow-y-auto"
            data-test="create-partner-modal-content"
          >
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
              data-test="create-partner-modal-form"
            />
          </div>
        }
        footer={
          <div
            className="flex justify-end gap-2"
            data-test="create-partner-modal-footer"
          >
            <Button
              data-test="create-partner-modal-cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Button
              data-test="create-partner-modal-submit-button"
              disabled={!isFormValid || isSubmitting}
              onClick={handleCreateNew}
            >
              {t("partners.createModal.createNew")}
            </Button>
          </div>
        }
        contentContainerClassname="w-[640px]"
      />
      <CreatePartnerContactModal
        isOpen={view === "partnerContacts"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("partner");
          }
        }}
        onSuccess={onPartnerContactCreation}
      />
      <UnsavedChangesWarningDialog
        {...warningDialogProps}
        data-test="create-partner-modal-unsaved-warning"
      />
    </>
  );
};

export default CreatePartnerModal;
